import React, { FC, useEffect, useMemo, useState } from "react";
import ConfigAccordion from "@/components/ConfigAccordion";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import { useAppTheme } from "@/utils/theme";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import dayjs from "dayjs";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useCreateProgram, useUpdateProgram } from "@/hooks/usePrograms";
import { useUserStore } from "@/hooks/useUserStore";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { ErrorResponse } from "@/domain/error.interface";
import {
  ContextualAgentFlowStatus,
  ProgramCheckInMethod,
  Programs,
} from "@/domain/programs.interface";
import { ProgramPayload } from "@/services/program.service";
import { OrganizationUnit } from "@/domain/organizations.interface";
import { useAllEducationalResources } from "@/hooks/useEducationalResources";
import {
  EducationalResource,
  resourceDisplayTitle,
} from "@/domain/educationalResource.interface";

type ProgramStatus = "PENDING" | "ACTIVE" | "COMPLETED" | "CANCELED";

type ContextualAgentFlowForm = {
  flowType: string;
  programDay: number | string;
  amount: number | string;
  status: ContextualAgentFlowStatus;
};

type ProgramFormData = {
  // General
  name: string;
  status: ProgramStatus;
  eventName: string;
  orgUnitIds: string[];
  // Parameters
  requiresCaseNumber: boolean;
  maxParticipants: number | string;
  budgetLimit: number | string;
  maxEarningsPerParticipant: number | string;
  startDate: string;
  endDate: string;
  // Configuration - Modules
  enabledJournalsModule: boolean;
  enabledJournalsBackofficeView: boolean;
  enabledServiceProvidersDirectoryModule: boolean;
  viewBackofficeApptsEnabled: boolean;
  // Configuration - Rewards
  enabledRewardUponAccountCreation: boolean;
  enabledRewardUponCheckIn: boolean;
  enabledRewardUponApptCreation: boolean;
  enabledJournalsReward: boolean;
  potentialRewardsValue: number | string;
  // Configuration - Appointment notifications
  enabledPushApptEveningBefore: boolean;
  timePushApptEveningBefore: number | string;
  enabledPushApptMorningBefore: boolean;
  timePushApptMorningBefore: number | string;
  enabledPushApptBefore: boolean;
  minutesPushApptBefore: number | string;
  enabledPushApptCheckInBefore: boolean;
  minutesPushApptCheckInBefore: number | string;
  enabledPushApptMissedFirst: boolean;
  minutesPushApptMissedFirst: number | string;
  enabledPushApptMissedSecond: boolean;
  minutesPushApptMissedSecond: number | string;
  enabledPushApptMissedThird: boolean;
  minutesPushApptMissedThird: number | string;
  enabledPushApptPostCheckInFirst: boolean;
  minutesPushApptPostCheckInFirst: number | string;
  enabledPushApptPostCheckInSecond: boolean;
  minutesPushApptPostCheckInSecond: number | string;
  enabledPushApptPostCheckInThird: boolean;
  minutesPushApptPostCheckInThird: number | string;
  enabledPushApptPostCheckInFourth: boolean;
  minutesPushApptPostCheckInFourth: number | string;
  enabledMissedAppointmentReason: boolean;
  enabledRewardMissedApptReason: boolean;
  enabledViewAppointmentMap: boolean;
  onboardPhoneNumber: string;
  studyArmConfirmationMessage: string;
  participantUserGuideURL: string;
  weeklyMissedRescheduleReminderEnabled: boolean;
  postCheckInRescheduleReminderEnabled: boolean;
  weeklyPostCheckinScheduleReminderEnabled: boolean;
  // Configuration - Contextual agent
  enabledContextualAgent: boolean;
  enabledRewardCAFlow: boolean;
  agentFlowNotOnWeekends: boolean;
  contextualAgentPostFirstApptFollowUpEnabled: boolean;
  contextualAgentHourPostFirstApptTrigger: number | string;
  contextualAgentFlows: ContextualAgentFlowForm[];
  // Configuration - Other
  onboardReleasedDate: string;
  programDays: number | string;
  specifyAppointmentReason: string;
  checkInMethod: ProgramCheckInMethod | "";
  // Configuration - Educational resources (PRET-2046). Saved at the payload ROOT.
  educationalResourcesIds: string[];
};

const filterResourceOptions = createFilterOptions<EducationalResource>({
  stringify: (option) => resourceDisplayTitle(option),
});

const PROGRAM_STATUSES: ProgramStatus[] = [
  "PENDING",
  "ACTIVE",
  "COMPLETED",
  "CANCELED",
];

const CHECK_IN_METHODS: ProgramCheckInMethod[] = [
  "LOCATION_ONLY",
  "QR_ONLY",
  "LOCATION_QR",
];

const optionalNumber = () =>
  yup
    .number()
    .transform((value, originalValue) => {
      if (
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
      ) {
        return undefined;
      }
      return typeof value === "number" && !isNaN(value) ? value : undefined;
    })
    .nullable()
    .optional();

const buildSchema = () =>
  yup.object().shape({
    // General
    name: yup.string().trim().required("Name is required"),
    status: yup
      .mixed<ProgramStatus>()
      .oneOf(PROGRAM_STATUSES)
      .required("Status is required"),
    eventName: yup.string().trim().optional(),
    orgUnitIds: yup
      .array()
      .of(yup.string().required())
      .min(1, "At least one organization is required")
      .required("At least one organization is required"),
    // Parameters
    requiresCaseNumber: yup.boolean().required(),
    maxParticipants: optionalNumber().min(0, "Must be 0 or greater"),
    budgetLimit: optionalNumber().min(0, "Must be 0 or greater"),
    maxEarningsPerParticipant: optionalNumber().min(0, "Must be 0 or greater"),
    startDate: yup.string().optional(),
    endDate: yup.string().optional(),
    // Modules
    enabledJournalsModule: yup.boolean().required(),
    enabledJournalsBackofficeView: yup.boolean().required(),
    enabledServiceProvidersDirectoryModule: yup.boolean().required(),
    viewBackofficeApptsEnabled: yup.boolean().required(),
    // Rewards
    enabledRewardUponAccountCreation: yup.boolean().required(),
    enabledRewardUponCheckIn: yup.boolean().required(),
    enabledRewardUponApptCreation: yup.boolean().required(),
    enabledJournalsReward: yup.boolean().required(),
    potentialRewardsValue: optionalNumber().min(0, "Must be 0 or greater"),
    // Notifications
    enabledPushApptEveningBefore: yup.boolean().required(),
    timePushApptEveningBefore: optionalNumber().min(0, "Must be 0 or greater"),
    enabledPushApptMorningBefore: yup.boolean().required(),
    timePushApptMorningBefore: optionalNumber().min(0, "Must be 0 or greater"),
    enabledPushApptBefore: yup.boolean().required(),
    minutesPushApptBefore: optionalNumber().min(0, "Must be 0 or greater"),
    enabledPushApptCheckInBefore: yup.boolean().required(),
    minutesPushApptCheckInBefore: optionalNumber().min(
      0,
      "Must be 0 or greater"
    ),
    enabledPushApptMissedFirst: yup.boolean().required(),
    minutesPushApptMissedFirst: optionalNumber().min(0, "Must be 0 or greater"),
    enabledPushApptMissedSecond: yup.boolean().required(),
    minutesPushApptMissedSecond: optionalNumber().min(
      0,
      "Must be 0 or greater"
    ),
    enabledPushApptMissedThird: yup.boolean().required(),
    minutesPushApptMissedThird: optionalNumber().min(0, "Must be 0 or greater"),
    enabledPushApptPostCheckInFirst: yup.boolean().required(),
    minutesPushApptPostCheckInFirst: optionalNumber().min(
      0,
      "Must be 0 or greater"
    ),
    enabledPushApptPostCheckInSecond: yup.boolean().required(),
    minutesPushApptPostCheckInSecond: optionalNumber().min(
      0,
      "Must be 0 or greater"
    ),
    enabledPushApptPostCheckInThird: yup.boolean().required(),
    minutesPushApptPostCheckInThird: optionalNumber().min(
      0,
      "Must be 0 or greater"
    ),
    enabledPushApptPostCheckInFourth: yup.boolean().required(),
    minutesPushApptPostCheckInFourth: optionalNumber().min(
      0,
      "Must be 0 or greater"
    ),
    enabledMissedAppointmentReason: yup.boolean().required(),
    enabledRewardMissedApptReason: yup.boolean().required(),
    enabledViewAppointmentMap: yup.boolean().required(),
    onboardPhoneNumber: yup.string().trim().optional(),
    // PRET-1901 (FR-14). NO max(): design 7a says "Length is not capped — the mobile screen
    // scrolls", and screen 4b exists to prove a long message still leaves CONTINUE reachable.
    studyArmConfirmationMessage: yup.string().optional(),
    participantUserGuideURL: yup.string().trim().optional(),
    weeklyMissedRescheduleReminderEnabled: yup.boolean().required(),
    postCheckInRescheduleReminderEnabled: yup.boolean().required(),
    weeklyPostCheckinScheduleReminderEnabled: yup.boolean().required(),
    // Contextual agent
    enabledContextualAgent: yup.boolean().required(),
    enabledRewardCAFlow: yup.boolean().required(),
    agentFlowNotOnWeekends: yup.boolean().required(),
    contextualAgentPostFirstApptFollowUpEnabled: yup.boolean().required(),
    contextualAgentHourPostFirstApptTrigger: optionalNumber()
      .integer("Must be an integer")
      .min(0, "Must be 0 or greater"),
    contextualAgentFlows: yup
      .array()
      .of(
        yup.object().shape({
          flowType: yup.string().trim().required("Flow type is required"),
          programDay: yup
            .number()
            .typeError("Must be a number")
            .required("Program day is required")
            .min(0, "Must be 0 or greater"),
          amount: yup
            .number()
            .typeError("Must be a number")
            .required("Amount is required")
            .min(0, "Must be 0 or greater"),
          status: yup
            .mixed<ContextualAgentFlowStatus>()
            .oneOf(["ACTIVE", "INACTIVE"])
            .required("Status is required"),
        })
      )
      .default([]),
    // Other
    onboardReleasedDate: yup
      .string()
      .oneOf(["", "readonly", "writing"])
      .optional(),
    programDays: optionalNumber().min(0, "Must be 0 or greater"),
    specifyAppointmentReason: yup.string().trim().optional(),
    checkInMethod: yup
      .mixed<ProgramCheckInMethod | "">()
      .oneOf(["", ...CHECK_IN_METHODS])
      .optional(),
    // PRET-2046 (FR-14): optional — an empty list is a valid save.
    educationalResourcesIds: yup
      .array()
      .of(yup.string().required())
      .default([]),
  });

const DEFAULT_VALUES: ProgramFormData = {
  name: "",
  status: "ACTIVE",
  eventName: "",
  orgUnitIds: [],
  requiresCaseNumber: false,
  maxParticipants: 0,
  budgetLimit: 0,
  maxEarningsPerParticipant: 0,
  startDate: "",
  endDate: "",
  enabledJournalsModule: false,
  enabledJournalsBackofficeView: false,
  enabledServiceProvidersDirectoryModule: false,
  viewBackofficeApptsEnabled: false,
  enabledRewardUponAccountCreation: false,
  enabledRewardUponCheckIn: false,
  enabledRewardUponApptCreation: false,
  enabledJournalsReward: false,
  potentialRewardsValue: 0,
  enabledPushApptEveningBefore: false,
  timePushApptEveningBefore: 0,
  enabledPushApptMorningBefore: false,
  timePushApptMorningBefore: 0,
  enabledPushApptBefore: false,
  minutesPushApptBefore: 0,
  enabledPushApptCheckInBefore: false,
  minutesPushApptCheckInBefore: 0,
  enabledPushApptMissedFirst: false,
  minutesPushApptMissedFirst: 0,
  enabledPushApptMissedSecond: false,
  minutesPushApptMissedSecond: 0,
  enabledPushApptMissedThird: false,
  minutesPushApptMissedThird: 0,
  enabledPushApptPostCheckInFirst: false,
  minutesPushApptPostCheckInFirst: 0,
  enabledPushApptPostCheckInSecond: false,
  minutesPushApptPostCheckInSecond: 0,
  enabledPushApptPostCheckInThird: false,
  minutesPushApptPostCheckInThird: 0,
  enabledPushApptPostCheckInFourth: false,
  minutesPushApptPostCheckInFourth: 0,
  enabledMissedAppointmentReason: false,
  enabledRewardMissedApptReason: false,
  enabledViewAppointmentMap: false,
  onboardPhoneNumber: "",
  studyArmConfirmationMessage: "",
  participantUserGuideURL: "",
  weeklyMissedRescheduleReminderEnabled: false,
  postCheckInRescheduleReminderEnabled: false,
  weeklyPostCheckinScheduleReminderEnabled: false,
  enabledContextualAgent: false,
  enabledRewardCAFlow: false,
  agentFlowNotOnWeekends: false,
  contextualAgentPostFirstApptFollowUpEnabled: false,
  contextualAgentHourPostFirstApptTrigger: 0,
  contextualAgentFlows: [],
  onboardReleasedDate: "",
  programDays: 0,
  specifyAppointmentReason: "",
  checkInMethod: "",
  educationalResourcesIds: [],
};

type ProgramModalProps = {
  open: boolean;
  onClose: () => void;
  program?: Programs | null;
};

const toDateInputValue = (value?: string | Date): string => {
  if (!value) return "";
  const d = dayjs(value);
  return d.isValid() ? d.format("YYYY-MM-DD") : "";
};

const toIsoDate = (value?: string): string | undefined => {
  if (!value) return undefined;
  const d = dayjs(value);
  return d.isValid() ? d.toISOString() : undefined;
};

const coerceNumber = (
  value: number | string | undefined
): number | undefined => {
  if (value === "" || value === null || value === undefined) return undefined;
  const n = typeof value === "number" ? value : Number(value);
  return isNaN(n) ? undefined : n;
};

const hasValue = (v: unknown): boolean => {
  if (v === undefined || v === null) return false;
  if (typeof v === "string") return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "boolean") return v === true;
  if (typeof v === "number") return !isNaN(v);
  return true;
};

type TabPanelProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
};

const TabPanel: FC<TabPanelProps> = ({ children, index, value }) => {
  if (value !== index) return null;
  return (
    <Box role="tabpanel" sx={{ pt: 2 }}>
      {children}
    </Box>
  );
};

const ProgramModal: FC<ProgramModalProps> = ({ open, onClose, program }) => {
  const theme = useAppTheme();
  const { showSnackBar } = useFeedBack();
  const isEditMode = !!program?._id;

  const [activeTab, setActiveTab] = useState(0);

  const createProgram = useCreateProgram();
  const updateProgram = useUpdateProgram();
  const isPending = createProgram.isPending || updateProgram.isPending;

  const { userInfo } = useUserStore();
  const currentUserId = userInfo?._id;

  const { organizations, isLoadingOrganizations } = useOrganizations({
    status: "ACTIVE",
    pageNumber: 0,
    pageSize: 500,
    sortField: "name",
    sortOrder: 1,
  });

  const availableOrganizations: OrganizationUnit[] = useMemo(
    () => organizations?.list || [],
    [organizations]
  );

  const {
    allEducationalResources,
    isLoadingAllEducationalResources,
    isAllEducationalResourcesLoaded: isResourceCatalogueLoaded,
    isFetchingAllEducationalResources: isResourceCatalogueFetching,
  } = useAllEducationalResources(open);

  const resourcesById = useMemo(
    () =>
      new Map<string, EducationalResource>(
        (allEducationalResources || []).map((r) => [r._id, r])
      ),
    [allEducationalResources]
  );

  const activeResources: EducationalResource[] = useMemo(
    () =>
      (allEducationalResources || [])
        .filter((r) => r.status === "ACTIVE")
        .sort((a, b) =>
          resourceDisplayTitle(a).localeCompare(resourceDisplayTitle(b))
        ),
    [allEducationalResources]
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ProgramFormData>({
    resolver: yupResolver(buildSchema()) as never,
    mode: "all",
    defaultValues: DEFAULT_VALUES,
  });

  const {
    fields: flowFields,
    append: appendFlow,
    remove: removeFlow,
  } = useFieldArray({
    control,
    name: "contextualAgentFlows",
  });

  useEffect(() => {
    if (!open) return;
    setActiveTab(0);
    if (program) {
      const p = program.parameters || {};
      const c = program.configuration || {};
      reset({
        name: program.name || "",
        status: (program.status as ProgramStatus) || "ACTIVE",
        eventName: program.eventName || "",
        orgUnitIds: Array.isArray(program.orgUnitIds)
          ? program.orgUnitIds
          : Array.isArray(program.orgUnitId)
            ? program.orgUnitId
            : [],
        requiresCaseNumber: !!p.requiresCaseNumber,
        maxParticipants: p.maxParticipants ?? 0,
        budgetLimit: p.budgetLimit ?? 0,
        maxEarningsPerParticipant: p.maxEarningsPerParticipant ?? 0,
        startDate: toDateInputValue(p.startDate),
        endDate: toDateInputValue(p.endDate),
        enabledJournalsModule: !!c.enabledJournalsModule,
        enabledJournalsBackofficeView: !!c.enabledJournalsBackofficeView,
        enabledServiceProvidersDirectoryModule:
          !!c.enabledServiceProvidersDirectoryModule,
        viewBackofficeApptsEnabled: !!c.viewBackofficeApptsEnabled,
        enabledRewardUponAccountCreation: !!c.enabledRewardUponAccountCreation,
        enabledRewardUponCheckIn: !!c.enabledRewardUponCheckIn,
        enabledRewardUponApptCreation: !!c.enabledRewardUponApptCreation,
        enabledJournalsReward: !!c.enabledJournalsReward,
        potentialRewardsValue: c.potentialRewardsValue ?? 0,
        enabledPushApptEveningBefore: !!c.enabledPushApptEveningBefore,
        timePushApptEveningBefore: c.timePushApptEveningBefore ?? 0,
        enabledPushApptMorningBefore: !!c.enabledPushApptMorningBefore,
        timePushApptMorningBefore: c.timePushApptMorningBefore ?? 0,
        enabledPushApptBefore: !!c.enabledPushApptBefore,
        minutesPushApptBefore: c.minutesPushApptBefore ?? 0,
        enabledPushApptCheckInBefore: !!c.enabledPushApptCheckInBefore,
        minutesPushApptCheckInBefore: c.minutesPushApptCheckInBefore ?? 0,
        enabledPushApptMissedFirst: !!c.enabledPushApptMissedFirst,
        minutesPushApptMissedFirst: c.minutesPushApptMissedFirst ?? 0,
        enabledPushApptMissedSecond: !!c.enabledPushApptMissedSecond,
        minutesPushApptMissedSecond: c.minutesPushApptMissedSecond ?? 0,
        enabledPushApptMissedThird: !!c.enabledPushApptMissedThird,
        minutesPushApptMissedThird: c.minutesPushApptMissedThird ?? 0,
        enabledPushApptPostCheckInFirst: !!c.enabledPushApptPostCheckInFirst,
        minutesPushApptPostCheckInFirst: c.minutesPushApptPostCheckInFirst ?? 0,
        enabledPushApptPostCheckInSecond: !!c.enabledPushApptPostCheckInSecond,
        minutesPushApptPostCheckInSecond:
          c.minutesPushApptPostCheckInSecond ?? 0,
        enabledPushApptPostCheckInThird: !!c.enabledPushApptPostCheckInThird,
        minutesPushApptPostCheckInThird: c.minutesPushApptPostCheckInThird ?? 0,
        enabledPushApptPostCheckInFourth: !!c.enabledPushApptPostCheckInFourth,
        minutesPushApptPostCheckInFourth:
          c.minutesPushApptPostCheckInFourth ?? 0,
        enabledMissedAppointmentReason: !!c.enabledMissedAppointmentReason,
        enabledRewardMissedApptReason: !!c.enabledRewardMissedApptReason,
        enabledViewAppointmentMap: !!c.enabledViewAppointmentMap,
        onboardPhoneNumber: c.onboardPhoneNumber ?? "",
        studyArmConfirmationMessage: c.studyArmConfirmationMessage ?? "",
        participantUserGuideURL: c.participantUserGuideURL ?? "",
        weeklyMissedRescheduleReminderEnabled:
          !!c.weeklyMissedRescheduleReminderEnabled,
        postCheckInRescheduleReminderEnabled:
          !!c.postCheckInRescheduleReminderEnabled,
        weeklyPostCheckinScheduleReminderEnabled:
          !!c.weeklyPostCheckinScheduleReminderEnabled,
        enabledContextualAgent: !!c.enabledContextualAgent,
        enabledRewardCAFlow: !!c.enabledRewardCAFlow,
        agentFlowNotOnWeekends: !!c.agentFlowNotOnWeekends,
        contextualAgentPostFirstApptFollowUpEnabled:
          !!c.contextualAgentPostFirstApptFollowUpEnabled,
        contextualAgentHourPostFirstApptTrigger:
          c.contextualAgentHourPostFirstApptTrigger ?? 0,
        contextualAgentFlows: Array.isArray(c.contextualAgentFlows)
          ? c.contextualAgentFlows.map((f) => ({
              flowType: f.flowType ?? "",
              programDay: f.programDay ?? 0,
              amount: f.amount ?? 0,
              status: (f.status as ContextualAgentFlowStatus) ?? "ACTIVE",
            }))
          : [],
        onboardReleasedDate: c.onboardReleasedDate ?? "",
        programDays: c.programDays ?? 0,
        specifyAppointmentReason: c.specifyAppointmentReason ?? "",
        checkInMethod: (c.checkInMethod as ProgramCheckInMethod) ?? "",
        educationalResourcesIds: Array.isArray(program.educationalResourcesIds)
          ? program.educationalResourcesIds
          : [],
      });
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [program, open, reset]);

  const buildPayload = (data: ProgramFormData): ProgramPayload => {
    const parameters = {
      requiresCaseNumber: data.requiresCaseNumber,
      maxParticipants: coerceNumber(data.maxParticipants),
      budgetLimit: coerceNumber(data.budgetLimit),
      maxEarningsPerParticipant: coerceNumber(data.maxEarningsPerParticipant),
      startDate: toIsoDate(data.startDate),
      endDate: toIsoDate(data.endDate),
    };

    const configuration = {
      enabledJournalsModule: data.enabledJournalsModule,
      enabledJournalsBackofficeView: data.enabledJournalsBackofficeView,
      enabledServiceProvidersDirectoryModule:
        data.enabledServiceProvidersDirectoryModule,
      viewBackofficeApptsEnabled: data.viewBackofficeApptsEnabled,
      enabledRewardUponAccountCreation: data.enabledRewardUponAccountCreation,
      enabledRewardUponCheckIn: data.enabledRewardUponCheckIn,
      enabledRewardUponApptCreation: data.enabledRewardUponApptCreation,
      enabledJournalsReward: data.enabledJournalsReward,
      potentialRewardsValue: coerceNumber(data.potentialRewardsValue),
      enabledPushApptEveningBefore: data.enabledPushApptEveningBefore,
      timePushApptEveningBefore: coerceNumber(data.timePushApptEveningBefore),
      enabledPushApptMorningBefore: data.enabledPushApptMorningBefore,
      timePushApptMorningBefore: coerceNumber(data.timePushApptMorningBefore),
      enabledPushApptBefore: data.enabledPushApptBefore,
      minutesPushApptBefore: coerceNumber(data.minutesPushApptBefore),
      enabledPushApptCheckInBefore: data.enabledPushApptCheckInBefore,
      minutesPushApptCheckInBefore: coerceNumber(
        data.minutesPushApptCheckInBefore
      ),
      enabledPushApptMissedFirst: data.enabledPushApptMissedFirst,
      minutesPushApptMissedFirst: coerceNumber(data.minutesPushApptMissedFirst),
      enabledPushApptMissedSecond: data.enabledPushApptMissedSecond,
      minutesPushApptMissedSecond: coerceNumber(
        data.minutesPushApptMissedSecond
      ),
      enabledPushApptMissedThird: data.enabledPushApptMissedThird,
      minutesPushApptMissedThird: coerceNumber(data.minutesPushApptMissedThird),
      enabledPushApptPostCheckInFirst: data.enabledPushApptPostCheckInFirst,
      minutesPushApptPostCheckInFirst: coerceNumber(
        data.minutesPushApptPostCheckInFirst
      ),
      enabledPushApptPostCheckInSecond: data.enabledPushApptPostCheckInSecond,
      minutesPushApptPostCheckInSecond: coerceNumber(
        data.minutesPushApptPostCheckInSecond
      ),
      enabledPushApptPostCheckInThird: data.enabledPushApptPostCheckInThird,
      minutesPushApptPostCheckInThird: coerceNumber(
        data.minutesPushApptPostCheckInThird
      ),
      enabledPushApptPostCheckInFourth: data.enabledPushApptPostCheckInFourth,
      minutesPushApptPostCheckInFourth: coerceNumber(
        data.minutesPushApptPostCheckInFourth
      ),
      enabledMissedAppointmentReason: data.enabledMissedAppointmentReason,
      enabledRewardMissedApptReason: data.enabledRewardMissedApptReason,
      enabledViewAppointmentMap: data.enabledViewAppointmentMap,
      onboardPhoneNumber: data.onboardPhoneNumber?.trim() || undefined,
      // Trimmed but NOT length-limited. Empty means "no message configured", which the mobile
      // screen handles by showing the arm block without a body — the field is not required.
      studyArmConfirmationMessage:
        data.studyArmConfirmationMessage?.trim() || undefined,
      participantUserGuideURL:
        data.participantUserGuideURL?.trim() || undefined,
      weeklyMissedRescheduleReminderEnabled:
        data.weeklyMissedRescheduleReminderEnabled,
      postCheckInRescheduleReminderEnabled:
        data.postCheckInRescheduleReminderEnabled,
      weeklyPostCheckinScheduleReminderEnabled:
        data.weeklyPostCheckinScheduleReminderEnabled,
      enabledContextualAgent: data.enabledContextualAgent,
      enabledRewardCAFlow: data.enabledRewardCAFlow,
      agentFlowNotOnWeekends: data.agentFlowNotOnWeekends,
      contextualAgentPostFirstApptFollowUpEnabled:
        data.contextualAgentPostFirstApptFollowUpEnabled,
      contextualAgentHourPostFirstApptTrigger: coerceNumber(
        data.contextualAgentHourPostFirstApptTrigger
      ),
      contextualAgentFlows: (data.contextualAgentFlows || []).map((f) => ({
        flowType: f.flowType.trim(),
        programDay: Number(f.programDay) || 0,
        amount: Number(f.amount) || 0,
        status: f.status,
      })),
      onboardReleasedDate: data.onboardReleasedDate || undefined,
      programDays: coerceNumber(data.programDays),
      specifyAppointmentReason:
        data.specifyAppointmentReason?.trim() || undefined,
      checkInMethod: data.checkInMethod || undefined,
    };

    const hasAnyParameter = Object.values(parameters).some((v) => hasValue(v));
    const hasAnyConfiguration = Object.values(configuration).some((v) =>
      hasValue(v)
    );

    const payload: ProgramPayload = {
      name: data.name.trim(),
      status: data.status,
      orgUnitIds: data.orgUnitIds,
      // PRET-2046: always sent (`[]` included) so clearing the selection is saved (AC3). Ids that
      // no longer resolve (deleted resources) are dropped — but only from a settled, fresh
      // catalogue; while it loads, refetches or after it failed, the stored ids are sent back
      // untouched, so a resource newer than a cached list is never wiped.
      educationalResourcesIds:
        isResourceCatalogueLoaded && !isResourceCatalogueFetching
          ? (data.educationalResourcesIds || []).filter((id) =>
              resourcesById.has(id)
            )
          : data.educationalResourcesIds || [],
    };

    if (data.eventName?.trim()) {
      payload.eventName = data.eventName.trim();
    }

    if (hasAnyParameter) {
      payload.parameters = parameters;
    }

    if (hasAnyConfiguration) {
      payload.configuration = configuration;
    }

    return payload;
  };

  const onSubmit = async (data: ProgramFormData) => {
    const payload = buildPayload(data);
    try {
      if (isEditMode && program?._id) {
        await updateProgram.mutateAsync({
          id: program._id,
          data: { ...payload, updatedBy: currentUserId },
        });
        showSnackBar("The program has been updated.", "success");
      } else {
        await createProgram.mutateAsync({
          ...payload,
          createdBy: currentUserId,
        });
        showSnackBar("The program has been created.", "success");
      }
      onClose();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred",
        "error"
      );
    }
  };

  const selectedOrgUnitIds = watch("orgUnitIds") || [];

  // PRET-2046 — resources picked for this programme. Options are the ACTIVE ones; an INACTIVE
  // assignment stays visible (greyed) and is kept on save; a deleted one resolves to nothing,
  // so it is not shown and is dropped on save.
  const selectedResourceIds = watch("educationalResourcesIds") || [];
  const selectedResources: EducationalResource[] = selectedResourceIds
    .map((id) => resourcesById.get(id))
    .filter((r): r is EducationalResource => !!r);
  // Inactive selections are passed as options too (MUI warns about a value that matches no
  // option) and filtered back out of the list, so they can never be picked anew.
  const resourceOptions: EducationalResource[] = [
    ...activeResources,
    ...selectedResources.filter((r) => r.status !== "ACTIVE"),
  ];
  const showNoResources =
    isResourceCatalogueLoaded && activeResources.length === 0;

  // Watchers for dependent "enabled" flags
  const wEveningBefore = watch("enabledPushApptEveningBefore");
  const wMorningBefore = watch("enabledPushApptMorningBefore");
  const wApptBefore = watch("enabledPushApptBefore");
  const wCheckInBefore = watch("enabledPushApptCheckInBefore");
  const wMissedFirst = watch("enabledPushApptMissedFirst");
  const wMissedSecond = watch("enabledPushApptMissedSecond");
  const wMissedThird = watch("enabledPushApptMissedThird");
  const wPostCheckInFirst = watch("enabledPushApptPostCheckInFirst");
  const wPostCheckInSecond = watch("enabledPushApptPostCheckInSecond");
  const wPostCheckInThird = watch("enabledPushApptPostCheckInThird");
  const wPostCheckInFourth = watch("enabledPushApptPostCheckInFourth");

  const renderBoolean = (
    name: keyof ProgramFormData,
    label: string,
    gridProps: { xs: number; sm?: number; md?: number } = { xs: 12, sm: 6 }
  ) => (
    <Grid item {...gridProps}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={
              <Switch
                checked={!!field.value}
                onChange={(_e, checked) => field.onChange(checked)}
                disabled={isPending}
              />
            }
            label={label}
          />
        )}
      />
    </Grid>
  );

  const renderNumber = (
    name: keyof ProgramFormData,
    label: string,
    disabled = false,
    gridProps: { xs: number; sm?: number; md?: number } = { xs: 12, sm: 6 }
  ) => (
    <Grid item {...gridProps}>
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            value={field.value ?? ""}
            onChange={(e) => field.onChange(e.target.value)}
            type="number"
            label={label}
            fullWidth
            disabled={isPending || disabled}
            error={!!fieldState.error}
            helperText={fieldState.error?.message || ""}
          />
        )}
      />
    </Grid>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={isPending}
      theme={theme}
      title={isEditMode ? "Edit Program" : "Create Program"}
      size={{
        width: "100%",
        minHeight: 640,
        maxHeight: 820,
        maxWidth: 880,
      }}
      content={
        <Box sx={{ width: "100%" }}>
          <Tabs
            value={activeTab}
            onChange={(_e, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              // Frame 7a's tab type, verbatim. NOT bold: an earlier pass set 700 on the developer's
              // "en negrita", and the spec they then supplied says 500 — so this is the correction,
              // and MUI's own Tab default happens to agree.
              // No fontFamily here. The spec names Roboto, but the theme already sets the family
              // app-wide — restating it on this one control detaches it from the theme, so a
              // future family change would leave these three tabs behind. Size, weight, tracking
              // and case are the parts that actually differ from MUI's default.
              "& .MuiTab-root": {
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "24px",
                letterSpacing: "0.4px",
                textTransform: "uppercase",
              },
              // #1A43D2 via menu.dark, not primary.main — primary.main is the lighter #355DE8.
              "& .MuiTab-root.Mui-selected": {
                color: "menu.dark",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "menu.dark",
              },
            }}
          >
            <Tab label="General" />
            <Tab label="Parameters" />
            <Tab label="Configuration" />
          </Tabs>

          <TabPanel value={activeTab} index={0}>
            <Grid container spacing={"20px"}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      label="Name"
                      fullWidth
                      required
                      error={!!errors.name}
                      helperText={errors.name?.message || ""}
                      disabled={isPending}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Status"
                      fullWidth
                      required
                      error={!!errors.status}
                      helperText={errors.status?.message || ""}
                      disabled={isPending}
                    >
                      {PROGRAM_STATUSES.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="eventName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      label="Event Name"
                      fullWidth
                      error={!!errors.eventName}
                      helperText={errors.eventName?.message || ""}
                      disabled={isPending}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={availableOrganizations}
                  loading={isLoadingOrganizations}
                  disabled={isPending}
                  getOptionLabel={(option) => option.name || option._id || ""}
                  isOptionEqualToValue={(option, value) =>
                    option._id === value._id
                  }
                  value={availableOrganizations.filter((o) =>
                    selectedOrgUnitIds.includes(o._id)
                  )}
                  onChange={(_e, selected) => {
                    setValue(
                      "orgUnitIds",
                      selected.map((o) => o._id),
                      { shouldDirty: true, shouldValidate: true }
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Organizations"
                      placeholder="Select organizations"
                      required
                      error={!!errors.orgUnitIds}
                      helperText={
                        (errors.orgUnitIds as { message?: string })?.message ||
                        ""
                      }
                    />
                  )}
                />
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Grid container spacing={"20px"}>
              {renderBoolean("requiresCaseNumber", "Requires case number", {
                xs: 12,
                sm: 12,
              })}
              {renderNumber("maxParticipants", "Max participants")}
              {renderNumber("budgetLimit", "Budget limit")}
              {renderNumber(
                "maxEarningsPerParticipant",
                "Max earnings per participant"
              )}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      type="date"
                      label="Start date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      disabled={isPending}
                      error={!!errors.startDate}
                      helperText={errors.startDate?.message || ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="endDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      type="date"
                      label="End date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      disabled={isPending}
                      error={!!errors.endDate}
                      helperText={errors.endDate?.message || ""}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            {/*
              Design 7a: each section of this tab is an ACCORDION item, not a flat heading. The tab
              carried six sections in one Grid and PRET-1901 added a seventh block, so the design
              collapses them and the coordinator opens the one they came for.

              The outer element is a Box rather than a Grid container: accordions are full-width
              blocks, and each ConfigAccordion supplies the Grid container its own <Grid item>
              children need. That is what leaves every section body below unchanged.
            */}
            <Box sx={{ width: "100%" }}>
              <ConfigAccordion title="Modules" testId="config-modules">
                {renderBoolean("enabledJournalsModule", "Journals module")}
                {renderBoolean(
                  "enabledJournalsBackofficeView",
                  "Journals backoffice view"
                )}
                {renderBoolean(
                  "enabledServiceProvidersDirectoryModule",
                  "Service providers directory module"
                )}
                {renderBoolean(
                  "viewBackofficeApptsEnabled",
                  "View backoffice appointments"
                )}
                {renderBoolean(
                  "enabledViewAppointmentMap",
                  "Show appointment location on map"
                )}
              </ConfigAccordion>

              <ConfigAccordion title="Rewards" testId="config-rewards">
                {renderBoolean(
                  "enabledRewardUponAccountCreation",
                  "Reward upon account creation"
                )}
                {renderBoolean(
                  "enabledRewardUponCheckIn",
                  "Reward upon check-in"
                )}
                {renderBoolean(
                  "enabledRewardUponApptCreation",
                  "Reward upon appointment creation"
                )}
                {renderBoolean("enabledJournalsReward", "Journals reward")}
                {renderBoolean(
                  "enabledRewardMissedApptReason",
                  "Reward on missed appointment reason"
                )}
                {renderNumber(
                  "potentialRewardsValue",
                  "Potential rewards value"
                )}
              </ConfigAccordion>

              <ConfigAccordion
                title="Appointment notifications"
                testId="config-appointment-notifications"
              >
                {renderBoolean(
                  "enabledPushApptEveningBefore",
                  "Push evening before"
                )}
                {renderNumber(
                  "timePushApptEveningBefore",
                  "Time (evening before)",
                  !wEveningBefore
                )}
                {renderBoolean(
                  "enabledPushApptMorningBefore",
                  "Push morning before"
                )}
                {renderNumber(
                  "timePushApptMorningBefore",
                  "Time (morning before)",
                  !wMorningBefore
                )}
                {renderBoolean(
                  "enabledPushApptBefore",
                  "Push before appointment"
                )}
                {renderNumber(
                  "minutesPushApptBefore",
                  "Minutes before",
                  !wApptBefore
                )}
                {renderBoolean(
                  "enabledPushApptCheckInBefore",
                  "Push check-in before"
                )}
                {renderNumber(
                  "minutesPushApptCheckInBefore",
                  "Minutes (check-in before)",
                  !wCheckInBefore
                )}
                {renderBoolean(
                  "enabledPushApptMissedFirst",
                  "Push missed (1st)"
                )}
                {renderNumber(
                  "minutesPushApptMissedFirst",
                  "Minutes (1st missed)",
                  !wMissedFirst
                )}
                {renderBoolean(
                  "enabledPushApptMissedSecond",
                  "Push missed (2nd)"
                )}
                {renderNumber(
                  "minutesPushApptMissedSecond",
                  "Minutes (2nd missed)",
                  !wMissedSecond
                )}
                {renderBoolean(
                  "enabledPushApptMissedThird",
                  "Push missed (3rd)"
                )}
                {renderNumber(
                  "minutesPushApptMissedThird",
                  "Minutes (3rd missed)",
                  !wMissedThird
                )}
                {renderBoolean(
                  "enabledPushApptPostCheckInFirst",
                  "Push post check-in (1st)"
                )}
                {renderNumber(
                  "minutesPushApptPostCheckInFirst",
                  "Minutes (1st post check-in)",
                  !wPostCheckInFirst
                )}
                {renderBoolean(
                  "enabledPushApptPostCheckInSecond",
                  "Push post check-in (2nd)"
                )}
                {renderNumber(
                  "minutesPushApptPostCheckInSecond",
                  "Minutes (2nd post check-in)",
                  !wPostCheckInSecond
                )}
                {renderBoolean(
                  "enabledPushApptPostCheckInThird",
                  "Push post check-in (3rd)"
                )}
                {renderNumber(
                  "minutesPushApptPostCheckInThird",
                  "Minutes (3rd post check-in)",
                  !wPostCheckInThird
                )}
                {renderBoolean(
                  "enabledPushApptPostCheckInFourth",
                  "Push post check-in (4th)"
                )}
                {renderNumber(
                  "minutesPushApptPostCheckInFourth",
                  "Minutes (4th post check-in)",
                  !wPostCheckInFourth
                )}
                {renderBoolean(
                  "enabledMissedAppointmentReason",
                  "Ask reason for missed appointment"
                )}
                {renderBoolean(
                  "weeklyMissedRescheduleReminderEnabled",
                  "Weekly missed reschedule reminder"
                )}
                {renderBoolean(
                  "postCheckInRescheduleReminderEnabled",
                  "Post check-in reschedule reminder"
                )}
                {renderBoolean(
                  "weeklyPostCheckinScheduleReminderEnabled",
                  "Weekly post check-in schedule reminder"
                )}

                {/*
                PRET-1901 (FR-14) — design 7a.

                A NEW "Account creation" group, placed HERE deliberately: between Appointment
                notifications and Contextual agent. The epic offered dropping the field into the
                existing "Other" group as the cheaper option, and 7a rejects it — "Other" is drawn
                in the same frame as its own separate group, so the design considered that
                alternative and chose this one.
              */}
              </ConfigAccordion>

              <ConfigAccordion
                title="Account creation"
                testId="config-account-creation"
                defaultExpanded
              >
                <Grid item xs={12}>
                  <Controller
                    name="studyArmConfirmationMessage"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value)}
                        label="Study-arm confirmation message"
                        helperText="Plain text, shown to the participant on the Account created screen under the programme name. Length is not capped — the mobile screen scrolls."
                        multiline
                        minRows={4}
                        fullWidth
                        disabled={isPending}
                        // Frame 7a outlines this field in #1A43D2. `!important` on all three
                        // states.
                        //
                        // The specificity arithmetic says it should not be needed — the emitted
                        // rules are 0,2,0 (resting) and 0,4,0 (focus) against MUI's 0,1,0 and
                        // 0,3,0, so they win on paper, and a CSS dump from a rendered instance
                        // confirmed those are the rules produced. The running app disagreed: with
                        // `!important` the focus state took the blue, and the moment it was removed
                        // the focus state lost it too. Observation beats the arithmetic, so it
                        // stays.
                        //
                        // Do not "clean this up" without checking the browser first — it has been
                        // removed once on the strength of the arithmetic and had to be restored.
                        //
                        // Resting is still reported grey by the developer even with `!important`,
                        // which no cascade rule can explain; that one is unresolved and needs the
                        // inspector on the fieldset.
                        //
                        // borderWidth is pinned because MUI widens a focused outline to 2px.
                        sx={{
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#1A43D2 !important",
                          },
                          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: "#1A43D2 !important",
                            },
                          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: "#1A43D2 !important",
                              borderWidth: "1px !important",
                            },
                        }}
                      />
                    )}
                  />
                </Grid>
              </ConfigAccordion>

              <ConfigAccordion
                title="Contextual agent"
                testId="config-contextual-agent"
              >
                {renderBoolean(
                  "enabledContextualAgent",
                  "Enable contextual agent"
                )}
                {renderBoolean("enabledRewardCAFlow", "Reward CA flow")}
                {renderBoolean(
                  "agentFlowNotOnWeekends",
                  "Agent flow not on weekends"
                )}
                {renderBoolean(
                  "contextualAgentPostFirstApptFollowUpEnabled",
                  "Post first appointment follow-up"
                )}
                {renderNumber(
                  "contextualAgentHourPostFirstApptTrigger",
                  "Hours after first appointment"
                )}

                <Grid item xs={12}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="labelLarge" component="div">
                      Contextual agent flows
                    </Typography>
                    <Button
                      variantType="save"
                      label="Add flow"
                      onClick={() =>
                        appendFlow({
                          flowType: "",
                          programDay: 0,
                          amount: 0,
                          status: "ACTIVE",
                        })
                      }
                      disabled={isPending}
                    />
                  </Stack>
                  <Stack spacing={2}>
                    {flowFields.length === 0 && (
                      <Typography variant="bodyMedium" color="text.secondary">
                        No flows configured.
                      </Typography>
                    )}
                    {flowFields.map((f, idx) => (
                      <Box
                        key={f.id}
                        sx={{
                          p: 2,
                          border: "1px solid",
                          borderColor: theme.palette.outline.main,
                          borderRadius: "8px",
                        }}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <Controller
                              name={`contextualAgentFlows.${idx}.flowType`}
                              control={control}
                              render={({ field, fieldState }) => (
                                <TextField
                                  {...field}
                                  value={field.value || ""}
                                  onChange={(e) =>
                                    field.onChange(e.target.value)
                                  }
                                  label="Flow type"
                                  fullWidth
                                  required
                                  disabled={isPending}
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message || ""}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={6} sm={2}>
                            <Controller
                              name={`contextualAgentFlows.${idx}.programDay`}
                              control={control}
                              render={({ field, fieldState }) => (
                                <TextField
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) =>
                                    field.onChange(e.target.value)
                                  }
                                  type="number"
                                  label="Program day"
                                  fullWidth
                                  required
                                  disabled={isPending}
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message || ""}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={6} sm={2}>
                            <Controller
                              name={`contextualAgentFlows.${idx}.amount`}
                              control={control}
                              render={({ field, fieldState }) => (
                                <TextField
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) =>
                                    field.onChange(e.target.value)
                                  }
                                  type="number"
                                  label="Amount"
                                  fullWidth
                                  required
                                  disabled={isPending}
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message || ""}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={10} sm={3}>
                            <Controller
                              name={`contextualAgentFlows.${idx}.status`}
                              control={control}
                              render={({ field, fieldState }) => (
                                <TextField
                                  {...field}
                                  select
                                  label="Status"
                                  fullWidth
                                  required
                                  disabled={isPending}
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message || ""}
                                >
                                  <MenuItem value="ACTIVE">Active</MenuItem>
                                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                                </TextField>
                              )}
                            />
                          </Grid>
                          <Grid
                            item
                            xs={2}
                            sm={1}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <IconButton
                              aria-label="Remove flow"
                              onClick={() => removeFlow(idx)}
                              disabled={isPending}
                              sx={{ color: theme.palette.error.main }}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </Box>
                    ))}
                  </Stack>
                </Grid>
              </ConfigAccordion>

              <ConfigAccordion title="Other" testId="config-other">
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="onboardReleasedDate"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value)}
                        label="Onboard released date mode"
                        fullWidth
                        disabled={isPending}
                      >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="readonly">Read only</MenuItem>
                        <MenuItem value="writing">Writing</MenuItem>
                      </TextField>
                    )}
                  />
                </Grid>
                {renderNumber("programDays", "Program days")}
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="specifyAppointmentReason"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value)}
                        label="Specify appointment reason"
                        fullWidth
                        disabled={isPending}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="onboardPhoneNumber"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value)}
                        label="Onboard phone number"
                        placeholder="+1 555-555-5555"
                        fullWidth
                        disabled={isPending}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="participantUserGuideURL"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value)}
                        label="Participant user guide URL"
                        placeholder="https://..."
                        fullWidth
                        disabled={isPending}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="checkInMethod"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value)}
                        select
                        label="Check-in method"
                        fullWidth
                        disabled={isPending}
                      >
                        <MenuItem value="">None</MenuItem>
                        {CHECK_IN_METHODS.map((m) => (
                          <MenuItem key={m} value={m}>
                            {m}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>
              </ConfigAccordion>

              {/* PRET-2046 (FR-12..FR-16, elements E1–E5). */}
              <ConfigAccordion
                title="Educational resources"
                testId="config-educational-resources"
              >
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    data-testid="program-educational-resources-select"
                    options={resourceOptions}
                    loading={isLoadingAllEducationalResources}
                    disabled={isPending || isLoadingAllEducationalResources}
                    filterSelectedOptions
                    filterOptions={(options, state) =>
                      filterResourceOptions(
                        options.filter((o) => o.status === "ACTIVE"),
                        state
                      )
                    }
                    getOptionLabel={(option) => resourceDisplayTitle(option)}
                    isOptionEqualToValue={(option, value) =>
                      option._id === value._id
                    }
                    value={selectedResources}
                    onChange={(_e, selected) => {
                      setValue(
                        "educationalResourcesIds",
                        selected.map((r) => r._id),
                        { shouldDirty: true, shouldValidate: true }
                      );
                    }}
                    renderTags={(tagValue, getTagProps) =>
                      tagValue.map((option, index) => {
                        const inactive = option.status !== "ACTIVE";
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            {...tagProps}
                            data-testid={`program-educational-resources-chip-${option._id}`}
                            label={
                              inactive
                                ? `${resourceDisplayTitle(option)} (inactive)`
                                : resourceDisplayTitle(option)
                            }
                            variant={inactive ? "outlined" : "filled"}
                            sx={
                              inactive
                                ? {
                                    color: "text.disabled",
                                    fontStyle: "italic",
                                  }
                                : undefined
                            }
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Educational resources"
                        placeholder="Select educational resources"
                        helperText={
                          showNoResources ? (
                            <span data-testid="program-educational-resources-empty">
                              There are no active educational resources yet.{" "}
                              {/* New tab, so the unsaved programme edits are kept (E5). */}
                              <Link
                                href="/educational-resources"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="program-educational-resources-link"
                              >
                                Educational Resources
                              </Link>
                            </span>
                          ) : (
                            "Select the educational resources participants of this programme will see."
                          )
                        }
                      />
                    )}
                  />
                </Grid>
              </ConfigAccordion>
            </Box>
          </TabPanel>
        </Box>
      }
      actions={
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={isPending}
            label="CANCEL"
          />
          <Button
            variantType="save"
            onClick={() => handleSubmit(onSubmit)()}
            disabled={isPending}
            isLoading={isPending}
            label="SAVE"
          />
        </Box>
      }
    />
  );
};

export default ProgramModal;
